import { isNil } from 'lodash';

import { INERT } from 'actions/shared.types';
import CONF from 'config/conf.singleton';
import ALLOWED_METHODS from 'constants/allowedMethods.const';
import CTYPE from 'constants/ctype.const';
import HDR from 'constants/httpHeader.const';
import H from 'constants/httpMethod.const';
import MIME from 'constants/mime.const';
import UMSG_TYPE from 'constants/umsg/type.const';
import { AUTH_API } from 'middleware/api';
import prepareBody from 'utils/backend/prepareBody.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromAnyToUmsgType from 'utils/converters/umsg/fromAnyToUmsgType.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import actionType from 'utils/mw/actionType.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFn from 'utils/predicates/function/isFn.util';
import isPositiveInteger from 'utils/predicates/number/isPositiveInteger.util';

const L = logger('endpoint');

const DEFAULT_PATTERN = '?';
const DEFAULT_VERSION = 2;

const fz = Object.freeze;

const prepareHeaders = (options) => {
  const mt = fromAnyToTrimmed(options?.mt);
  const lt = fromAnyToTrimmed(options?.mt);
  const ctype = fromAnyToTrimmed(options?.ctype) || CTYPE.json;

  const headers = { 'Content-Type': MIME[ctype] || MIME.text };

  if (mt) headers[HDR.mt] = `Bearer ${mt}`;

  // NOTE: wrong naming convention was used, instead of Lock-Token
  if (lt) headers[HDR.lt] = lt;

  return { headers: fz(headers), ctype };
};

const prepareParams = (options) => {
  // preventive measure, a shallow copy that will get frozen later as to not modify the original params object
  const params = { ...asObject(options?.params) };

  // simplistic way of replacing the missing salonId and a flag check for possible opt-out case
  // SEE: src/middleware/api.middleware.js
  // SEE: src/utils/links/linkTo.util.js
  if (void 1 === params.salonId && !options?.omitSalonId) {
    params.salonId = ':salonId';
  }

  // NOTE: freeze the params object, so it can't be changed after this point
  fz(params);

  return params;
};

const endpoint = (method, pattern, options) => {
  const o = asObject(options);

  const m = ALLOWED_METHODS[fromAnyToString(method)] || L.warn('()', 'Forbidden method:', method, 'will use:', H.get);
  const p = fromAnyToString(pattern) || L.warn('()', 'Missing pattern:', pattern, 'will use:', DEFAULT_PATTERN);

  const params = prepareParams(o);

  const version = isPositiveInteger(o.v) ? o.v : DEFAULT_VERSION;
  const maxRetries = fromAnyToInteger(o.maxRetries) || void 1;
  const retryDelay = fromAnyToInteger(o.retryDelay) || void 1;
  const timeout = fromAnyToInteger(o.timeout) || void 1;

  // NOTE: functions are more complex, so just pass the same value, don't pass just the type
  const umsgType = fromAnyToUmsgType(o.umsg);
  const umsg = UMSG_TYPE.fn === umsgType ? o.umsg : umsgType;

  const auth = isNil(o.auth) ? true : fromAnyToBool(o.auth);
  const pin = isNil(o.pin) ? true : fromAnyToBool(o.pin);
  const base = fromAnyToTrimmed(o.base) || CONF.apiRoot;
  const name = fromAnyToTrimmed(o.name) || fromAnyToTrimmed(o.ATAG) || fromAnyToTrimmed(o.AT?.name);

  const inert = fromAnyToBool(o.inert);
  const at = inert ? INERT : o.at || o.AT || o.atype;
  const atype = at || L.warn('()', 'Invalid action type:', at, 'will use:', actionType(name));
  const types = fz([atype.init, atype.pass, atype.fail, atype.done]);

  const data = o.data;
  const toDto = isFn(o.toDto) ? o.toDto : void 1;
  const fromDto = isFn(o.fromDto) ? o.fromDto : void 1;

  const { headers, ctype } = prepareHeaders(o);

  const isMissingParams = o.isMissingParams;
  const path = isMissingParams ? DEFAULT_PATTERN : linkTo({ pattern: p, params, fault: DEFAULT_PATTERN });

  const queryKey = [name || '', ...(isFullArray(o.queryKey) ? o.queryKey : [params.salonId, m, path])];

  const v2 = {
    v: version,
    name,
    atype,
    ctype,
    method: m,
    path,
    base,
    headers,
    auth,
    pin,
    data,
    params,
    isMissingParams,
    maxRetries,
    retryDelay,
    timeout,
    umsg,
    inert,
    fromDto,
    toDto,
    queryKey,
    mock: o.mock,
  };

  if (version > 2) return fz(v2);

  const bodyResult = prepareBody({ data, ctype, method: m });

  return fz({
    ...(name ? { ATAG: name } : void 1), // legacy support

    mock: o.mock,

    ...v2,

    // legacy support
    [AUTH_API]: {
      authenticated: auth,
      api_root: base,
      pinSafe: !pin,
      endpoint: path,
      config: fz({ headers, method: m, ...bodyResult }),
      types,
      params,
      maxRetries,
      retryDelay,
      mock: o.mock,
    },
  });
};

const endpointWrapped = (...args) => {
  try {
    return endpoint(...args);
  } catch (e) {
    L.debug('endpointWrapped()', ...args, '->', e);
    throw e;
  }
};

export default endpointWrapped;
